import LandingFooter from '@/components/LandingFooter.tsx'
import LandingHeader from '@/components/LandingHeader.tsx'
import { languageBasedOnTimezone } from '@/utils/language.ts'
import { useEffect } from 'react'
import styles from './PolicyPage.module.scss'

export const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      <div className={styles.policyContainer}>
        <div className={styles.topSection}>
          <LandingHeader language={languageBasedOnTimezone()} selectLanguageEnabled={false} />
        </div>
        <div className={styles.contentsSection}>
          <div className={styles.titleContainer}>
            <h1>PRIVACY POLICY</h1>
            <div className={styles.dates}>Effective Date: December 14, 2024</div>
            <div className={styles.dates}>Last Updated: April 24, 2025</div>
            <div>
              <p>
                Welcome to Bekind Labs LLC (“Bekind Labs,” “we,” “us,” or “our”). We recognize the importance of privacy
                and are committed to protecting your personal information. This Privacy Policy explains how we collect,
                use, disclose, and safeguard your data when you interact with us. It applies to information collected
                when you access or use our products and services, visit our websites, support channels, attend our
                events (collectively, the “Services”), or otherwise engage with us — except where a different privacy
                policy is explicitly provided. Our “Services” encompass all our platforms, including but not limited to
                Tracker Boot, the agile project management platform operated by Bekind Labs, as well as any other
                software, tools, websites, or offerings we provide. This Privacy Policy applies in particular to your
                use of Tracker Boot, available at <a href="https://trackerboot.com">&nbsp;https://trackerboot.com</a>,
                and any related services offered by Bekind Labs.
              </p>
              <p>
                This policy further details your rights and choices regarding the processing of your personal data,
                including your ability to access, update, or object to certain uses of your information. By accessing or
                using any of our Services, you acknowledge and agree to the terms outlined in this Privacy Policy
                between you and us. If you do not agree with this Privacy Policy, you must refrain from accessing or
                using our Services. Where we provide the Services under contract with an organization (for example, your
                employer), that organization controls the information processed by the Services. This policy does not
                apply to the extent we process personal information in the role of a processor on behalf of such
                organizations. For more information, please see “Notice to End Users” below.
              </p>
            </div>
          </div>
          <div className={styles.contentsContainer}>
            <h2>Information we collect</h2>
            <p>
              We collect information about you when you provide it to us, when you use our Services, and when other
              sources provide it to us, as further described below.
            </p>
            <h3>Information You Provide to Us</h3>
            <ul>
              <li>
                <span>Account and Profile Information</span>Bekind Labs collects information directly from you when you
                input data into our Services or otherwise provide it through interactions with our platforms. When you
                register for an account, update your profile, configure preferences, subscribe to services, or make
                purchases, we collect personal details such as your name, email address, company information, and
                payment-related information if applicable. Account credentials, such as registration details, passwords,
                and authentication tokens, are also collected to ensure secure access to our Services. If you subscribe
                to paid services, we collect billing information, including designated billing representatives and
                payment details, which are processed securely through third-party payment gateways. Additionally, users
                may choose to provide optional profile details such as a display name, profile photo, or job title,
                which may be displayed within the Services. User preferences, including selected settings and
                configurations, may be tracked to personalize the experience within our Services.
              </li>
              <li>
                <span>Content You Provide Through Our Products & Services</span>Bekind Labs collects and stores content
                that you create, save, or share within the Services. This includes, but is not limited to, project
                management data, task descriptions, comments, attachments, and collaboration updates. Any files uploaded
                within the Services, as well as feedback or reports submitted through our platforms, are also collected.
              </li>
              <li>
                <span>Content You Provide Through Our Websites</span>Bekind Labs collects content that you submit
                through our websites, our owned and operated platforms and social media channels. This includes any
                information provided when submitting feedback, filling out forms, responding to surveys, participating
                in contests, promotions, or events, or engaging in interactive features such as forums and blogs.
              </li>
              <li>
                <span>Information You Provide Through Our Support Channels</span>When you engage with Bekind Labs’
                customer support services, we collect details necessary to provide assistance. This includes your
                contact information, descriptions of the issue encountered, and any documentation, screenshots, or other
                supporting materials that facilitate resolution. Whether through support tickets, email correspondence,
                or direct conversations with representatives, the information provided is used solely for
                troubleshooting and improving service quality.
              </li>
              <li>
                <span>Payment Information</span>Bekind Labs collects and processes billing and payment details when you
                register for paid services. This includes the name and contact information of designated billing
                representatives, invoice details, and payment transaction data. Payments are securely handled through
                third-party financial service providers, and while we do not store complete payment card details on our
                servers, we retain transaction records for accounting, dispute resolution, and compliance purposes.
              </li>
            </ul>
            <h3>Information We Collect Automatically When You Use the Services</h3>
            <ul>
              <li>
                <span>Your use of the Services</span>Bekind Labs automatically collects certain technical and usage data
                when you access, browse, or interact with our Services. This includes tracking your engagement with
                features, links clicked, search queries, file uploads, and collaboration activities. We analyze data
                related to project activity, task progress, and interactions with team members to enhance the
                functionality of our Services.
              </li>
              <li>
                <span>Device and Connection Information</span>Bekind Labs collects details about the devices and
                networks used to access our Services. This includes information about your operating system, browser
                type, IP address, referring URLs, device identifiers, and connection status. Crash reports and
                diagnostic logs may also be collected to improve service reliability and address technical issues.
                Location data, if enabled on your device, may be processed to optimize performance and personalize user
                experience. The extent of data collected depends on the type of device and the settings configured by
                the user.
              </li>
              <li>
                <span>Cookies and Other Tracking Technologies</span>Bekind Labs employs cookies, tracking pixels, and
                analytics tools such as Google Analytics to monitor site traffic, enhance user experience, and improve
                overall service efficiency. These tracking technologies help us understand how users navigate our
                platforms and interact with content. Users may manage or disable cookies through their browser settings.
                For more information about how we use cookies and your options to manage them, please refer to our
                Cookie Policy at https://trackerboot.com/policies/cookie-policy.
              </li>
            </ul>
            <h3>Information We Receive from Other Sources</h3>
            <p>
              Bekind Labs may receive additional information about users from various sources, including other Service
              users, third-party integrations, business partners, and publicly available records. Other users may
              provide information about you when they invite you to collaborate, mention you in a project, or designate
              you as a billing or technical contact. If you integrate third-party services such as Google, GitHub, or
              Slack, we receive relevant account details and content necessary to facilitate these integrations. The
              information shared depends on the privacy settings and permissions granted within those third-party
              services.
            </p>
            <ul>
              <li>
                <span>Other Users of the Services</span>Other users of our Services may provide information about you
                when they submit content through the Services. For example, you may be mentioned in a project or user
                story update, invited to collaborate on a backlog, or included in communications regarding a shared
                workspace. Bekind Labs may receive your contact details from other users who provide them when
                designating you as a collaborator, team member, or administrator.
              </li>
              <li>
                <span>Other Services You Link to Your Account</span>Bekind Labs may receive information when you or your
                administrator integrate third-party applications or link external services to your account.
                Additionally, if you log into our Services using Google credentials, which is a login method provided by
                Bekind Labs, we may receive your name and email address based on your Google profile settings. Likewise,
                integrations with productivity tools, calendar applications, or cloud storage services may involve data
                exchanges necessary to enable these connections. The extent of shared information depends on the
                permissions configured in the respective third-party services.
              </li>
              <li>
                <span>Bekind Labs Partners and Affiliates</span>Bekind Labs collaborates with industry partners and
                affiliates who provide consulting, implementation, and training services. Some partners also assist in
                marketing, lead generation, and reselling our Services. As part of these business relationships, we may
                receive billing details, contact information, event participation records, and insights regarding
                product interest.
              </li>
              <li>
                <span>Advertising and Market Research Partners</span>Bekind Labs may collect information from
                advertising networks, data providers, and market research firms to enhance targeted outreach and improve
                our Services. This may include details about user engagement with advertisements, online behavior
                patterns, and demographic insights obtained through lawful sources.
              </li>
            </ul>
          </div>
          <div className={styles.contentsContainer}>
            <h2>How We Use the Information We Collect</h2>
            <p>
              Bekind Labs processes the information we collect based on the specific Services you use, how you interact
              with them, and any preferences you have communicated. The following section details the purposes for which
              we utilize your data.
            </p>
            <p>
              <span>Providing and Personalizing the Services</span> We use the collected information to operate,
              maintain, and enhance our Services, including processing transactions, verifying user identities,
              providing customer support, and ensuring the seamless functionality of our platforms. For instance, your
              name and profile picture may be displayed to other users within the Services to facilitate collaboration.
              Our Services incorporate personalized features to enhance your experience, boost productivity, and improve
              teamwork by analyzing your activities and those of your team. This enables us to generate relevant search
              results, curated activity feeds, customized notifications, and relevant recommendations. If you utilize
              multiple Bekind Labs Services, we integrate data from various products to create a cohesive experience,
              allowing you to access information seamlessly across our platforms. We may also personalize communications
              and feature recommendations based on your interaction history. If you wish to opt out of such
              personalization, you may submit a request to info@bekindlabs.com.
            </p>
            <p>
              <span>Research and Development</span> Bekind Labs is continuously working to improve our Services, making
              them more intelligent, secure, and integrated. We use aggregated insights and behavioral trends to
              troubleshoot technical issues, optimize functionality, identify usage patterns, and develop new features
              and technologies. For example, in the future, we may analyze how users interact with features like AI
              assistance to improve collaboration recommendations. Our systems may also aggregate search queries to
              enhance the accuracy of auto-suggestions. In some instances, these insights are applied across multiple
              Services to refine interconnected experiences. Certain new features may be tested with a limited group of
              users before being released to all customers.
            </p>
            <p>
              <span>Communication Regarding the Services</span> We use your contact details to send essential
              communications via email, in-app notifications, or other channels, including confirmations of purchases,
              reminders of subscription expirations, responses to inquiries, and updates related to service maintenance,
              security alerts, and administrative notices if applicable. Notifications are also sent when you or others
              interact within the Services, such as when you are mentioned in a user story, assigned a user story, or
              added to a collaborative workspace. Certain product interactions may automatically trigger feature
              recommendations or third-party integrations to enhance workflow efficiency. Additionally, we provide
              onboarding assistance and usage guidance to help users maximize the value of our Services. These
              transactional communications are an integral part of the Service and, in most cases, cannot be opted out
              of. Where an opt-out option is available, it will be provided within the communication itself or in your
              account settings.
            </p>
            <p>
              <span>Marketing, Promotion, and User Engagement</span> Bekind Labs may use your contact details and
              interaction history to send promotional messages via email or other communication channels, as well as to
              display advertisements on third-party platforms. These marketing communications may be based on
              interactions such as ad impressions, product engagement, or participation in surveys, and are designed to
              keep you informed about new features, industry trends, exclusive offers, and events relevant to you. Our
              marketing efforts also include personalized outreach based on your usage of our Services. If you wish to
              opt out of promotional communications, you can manage your preferences by following the instructions in
              the emails received(e.g., unsubscribe).
            </p>
            <p>
              <span>Customer Support and Service Improvement</span> We use collected information to diagnose and resolve
              technical issues, address user concerns, analyze service failures, and enhance platform reliability. In
              cases where you grant us explicit permission, we may share relevant details with external technical
              specialists to assist with complex support inquiries.
            </p>
            <p>
              <span>Security and Fraud Prevention</span> Bekind Labs processes data to ensure the security and integrity
              of its Services. This includes verifying accounts, monitoring user activity, detecting and preventing
              unauthorized access, responding to potential security threats, and investigating any fraudulent,
              deceptive, or illegal activity. We also analyze service usage to enforce compliance with our terms and
              policies. In the event of a Security Incident, Bekind Labs will notify the Customer without undue delay,
              and, where feasible, within seventy-two (72) hours of becoming aware of the incident. Notification will be
              sent via email to the relevant contacts designated by the Customer.
            </p>
            <p>
              <span>Legal and Business Protections</span> Where necessary, we use personal data to comply with legal
              obligations, regulatory requirements, and audit functions. This includes responding to lawful government
              requests, protecting our intellectual property rights, enforcing contract terms, and ensuring compliance
              with applicable data protection laws. If Bekind Labs is involved in a corporate transaction such as a
              merger, acquisition, or sale, user data may be transferred to the acquiring entity as part of the business
              operation.
            </p>
            <p>
              <span>Processing with User Consent</span> Where specific consent is required, we use your data for
              purposes explicitly agreed upon by you. This may include publishing customer testimonials, featuring user
              stories in marketing campaigns, or participating in research studies. Users retain the right to withdraw
              consent at any time by contacting info@bekindlabs.com.
            </p>
            <p>
              <span>Legal Bases for Processing (Applicable to EEA Users)</span> For individuals residing in the European
              Economic Area (EEA), our data processing activities are conducted under lawful bases outlined in the
              General Data Protection Regulation (GDPR). The specific legal grounds on which we process personal data
              include:
            </p>
            <ul>
              <li>
                • Contractual Necessity – Processing is required to deliver the requested Services, including account
                management, authentication, support, and security.
              </li>
              <li>
                • Legitimate Interests – Processing supports essential business functions such as improving product
                usability, enhancing security, conducting research and development, and marketing new features, provided
                that such interests are not overridden by your privacy rights.
              </li>
              <li>
                • Legal Compliance – We process personal data as required to fulfill legal obligations, including
                regulatory compliance, taxation, and law enforcement cooperation.
              </li>
              <li>
                • User Consent – In cases where explicit consent is required (e.g., for marketing communications or
                non-essential data collection), users have the right to withdraw consent at any time without affecting
                prior lawful processing.
              </li>
            </ul>
            <p>
              If you object to data processing based on legitimate interests, your ability to use certain features of
              the Services may be impacted. Users may submit requests to exercise their data protection rights,
              including access, correction, restriction, and deletion, by contacting info@bekindlabs.com. Bekind Labs
              remains committed to upholding international data privacy standards, including the Act on the Protection
              of Personal Information (APPI) in Japan, Personal Information Protection Act (PIPA) in South Korea, and
              the California Consumer Privacy Act (CCPA) in the United States.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>How We Disclose Information We Collect</h2>
            <p>
              Bekind Labs provides collaboration tools designed to enhance productivity and teamwork. To ensure seamless
              functionality, we may disclose information through the Services and to certain third parties under
              specific circumstances. The ways in which we disclose information are outlined below, including
              disclosures that may occur in the event of a business transfer. It is important to note that information
              about you, your devices, and your activities, when collected through third-party cookies, pixels, tags, or
              similar tracking technologies, may be subject to privacy regulations under applicable data protection
              laws, including Japan’s Act on the Protection of Personal Information (APPI), the EU’s General Data
              Protection Regulation (GDPR), and other relevant laws in jurisdictions where Bekind Labs operates. Bekind
              Labs does not sell personal information for monetary consideration. However, certain activities, such as
              using third-party tracking technologies for personalized advertising, may be classified as “sharing” under
              certain privacy laws, such as the California Consumer Privacy Act (CCPA) and the California Privacy Rights
              Act (CPRA). Where required by applicable laws, we will comply with relevant legal obligations.
            </p>
            <p>
              <span>Disclosures to Other Service Users</span>When you use our Services, certain information about you
              may be disclosed to other users of the Service.
            </p>
            <p>
              <span>For Collaboration Purposes</span>Tracker Boot and other Services allow users to create, share, and
              collaborate on content, which may contain personal information. Depending on the configured settings, you
              may grant permissions to other users to view, edit, share, or download content. Some collaboration
              features display portions of your profile information to other users when you engage with content within
              the Services. For instance, if you comment on a Tracker Boot user story, your name will be displayed
              alongside your comment, allowing other authorized users to recognize the contributor. Similarly, when you
              participate in a project, your name will be listed as a project member, and users with the appropriate
              permissions can view your profile details.
            </p>
            <p>
              <span>Managed Accounts and Administrators</span>If you register for or access our Services using an email
              address associated with an organization (such as your employer) and that organization owns the domain,
              certain information about you—including your name, profile picture, contact details, content
              contributions, and account usage history—may become accessible to the organization’s administrator and
              other authorized users within the same domain. If your organization establishes an account or site,
              administrators may manage and oversee user activity within the Services. Additionally, if you are an
              administrator responsible for managing a site or a group of users, we may disclose your contact details to
              other Service users to facilitate Service-related inquiries and requests.
            </p>
            <p>
              <span>Disclosure of Information to Third Parties</span>Bekind Labs may disclose information to third
              parties that support the operation, provision, enhancement, integration, customization, support, and
              marketing of our Services. However, this excludes text messaging originator opt-in data and consent, which
              will not be shared with any third parties, except for aggregators and service providers that facilitate
              text messaging services.
            </p>
            <p>
              <span>Service Providers</span>We engage third-party service providers to assist with various operational
              functions, such as website and application development, hosting, maintenance, backup, storage, virtual
              infrastructure, payment processing, analytics, marketing, and other essential services other services for
              us, which may require them to access or use information about you. In some cases, these service providers
              may require access to certain information to perform these functions on our behalf. When this occurs, they
              do so under our strict instructions, in compliance with appropriate security and confidentiality protocols
              designed to protect your information.
            </p>
            <p>
              <span>Bekind Labs Partners</span>Bekind Labs collaborates with third parties that provide consulting,
              sales, support, and technical services to help implement and deliver customer solutions within the
              Services. We may disclose your information to these third parties as necessary for specific services, such
              as billing and collections, localized support, and customized solutions. Additionally, we may share
              information with these partners when you have explicitly agreed to such disclosure.
            </p>
            <h3>Disclosure to Third-Party Applications, Services, and Legal Authorities</h3>
            <p>
              <span>Third-Party Applications</span>Users, administrators, or other Service users may choose to enhance
              functionality or customize the behavior of the Services by integrating third-party applications. Such
              integrations may grant third-party applications access to your account and associated information,
              including your name, email address, and any content you utilize within those applications. If you are
              listed as an administrator, technical contact, or billing contact for an account, Bekind Labs may disclose
              your details to the third-party application provider at the time of installation. Bekind Labs does not
              control the policies or procedures of third-party applications, and this Privacy Policy does not extend to
              their handling of your information. We strongly recommend reviewing the privacy policies of third-party
              providers before connecting or using their applications and services to understand their data collection
              and usage practices. If you do not wish to share your information with a third-party application, you
              should refrain from installing or should uninstall the application.
            </p>
            <p>
              <span>Links to Third-Party Websites</span>Our Services may contain links to third-party websites or
              external services with privacy practices distinct from those of Bekind Labs. If you choose to submit
              personal information to any of these third-party websites, your data will be subject to their privacy
              policies, not ours. We encourage you to carefully review the privacy policies of any external websites or
              services you access.
            </p>
            <p>
              <span>Third-Party Services</span>Bekind Labs may provide opportunities for users to interact with or
              disclose information to third-party services through our platform. For example, users may have the option
              to log into the Services using authentication from a third-party service, such as Google. When you
              actively engage with these third-party services, we may disclose certain information, including contact
              details, identification, demographic data, and device-related information, in accordance with your privacy
              settings on the respective third-party service. We strongly encourage you to review the privacy policies
              and settings of such services to understand how your information may be used.
            </p>
            <p>
              <span>Disclosure with Your Consent</span>Bekind Labs may disclose your information to third parties when
              you have provided explicit consent for such disclosure. For instance, we may feature personal testimonials
              from satisfied customers on our public websites. With your prior approval, we may include your name
              alongside the testimonial.
            </p>
            <p>
              <span>Compliance with Legal Obligations and Enforcement of Rights</span>In certain circumstances, Bekind
              Labs may be required to disclose your information to third parties if we determine, in good faith, that
              such disclosure is reasonably necessary to:
            </p>
            <ul>
              <li>
                (a) comply with applicable laws, regulations, legal processes, or government requests, including those
                related to national security;
              </li>
              <li>(b) enforce our agreements, policies, and terms of service;</li>
              <li>(c) protect the security, integrity, or functionality of our products and services;</li>
              <li>
                (d) safeguard Bekind Labs, our customers, or the public from harm, fraudulent activity, or illegal
                conduct; or
              </li>
              <li>
                (e) respond to an emergency where disclosure is necessary to prevent death or serious bodily harm to an
                individual.
              </li>
            </ul>
            <p>
              Bekind Labs takes the protection of user data seriously and only discloses information when legally
              required or when it is essential to ensure compliance and security.
            </p>
            <h3>Disclosure to Affiliated Companies and Business Transfers</h3>
            <p>
              <span>Affiliated Companies</span>Bekind Labs may share the information it collects with its affiliated
              companies and, where applicable, with prospective affiliates. Affiliated companies are entities that are
              owned, controlled, or operated by Bekind Labs. Any such disclosures will be conducted in accordance with
              this Privacy Policy, ensuring consistent data protection and compliance with applicable privacy laws.
            </p>
            <p>
              <span>Bekind Labs Corporate Affiliates</span>We may disclose information about you to other Bekind Labs
              affiliates for the purpose of operating, enhancing, and improving our products and services, including
              Tracker Boot. This enables us to provide you with access to additional Bekind Labs-affiliated services
              that may be relevant to your needs. Such disclosures are made solely to facilitate a seamless and
              integrated user experience across our affiliated services while maintaining the same level of data
              protection and privacy.
            </p>
            <p>
              <span>Business Transfers</span>Bekind Labs may share or transfer information collected under this Privacy
              Policy in the event of a merger, acquisition, corporate financing, restructuring, or sale of all or a
              portion of our business or assets to another entity. If such a transaction occurs, you will be notified in
              advance through email and/or a prominent notice within the Services before the transfer is finalized. As
              part of this process, you will have the opportunity to review and manage your data preferences in
              accordance with relevant data protection laws. Your personal information will continue to be handled
              securely and in compliance with this Privacy Policy and applicable legal requirements, unless you provide
              explicit consent for an alternative arrangement.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>How We Store and Secure Collected Information</h2>
            <h3>Information Storage and Security</h3>
            <p>
              Bekind Labs employs industry-standard technical and organizational measures to safeguard the information
              we store. Users of Tracker Boot and other Bekind Labs services can refer to the Tracker Boot Agreement’s
              applicable
              <a href=" https://trackerboot.com/policies/data-processing-addendum">&nbsp;Data Processing Addendum</a>
              for further details on data storage and handling practices. While we implement security measures designed
              to protect your information, no system is entirely immune to security threats. Due to the inherent risks
              associated with the Internet, absolute security of data transmitted online or stored within our systems
              cannot be guaranteed. Despite our best efforts, there remains a possibility of unauthorized access,
              intrusion, or data exposure.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>Data Retention Policy</h2>
            <p>
              The retention period for your information depends on the type of data and the purposes for which it is
              processed. Once the applicable retention period expires, we will either delete or anonymize your
              information. If immediate deletion is not technically feasible (such as in the case of data stored in
              backup archives), we will securely isolate and store the data until deletion is possible.
            </p>
            <p>
              <span>Account Information</span> We retain your account information for as long as your account remains
              active, and for a reasonable period thereafter in case you choose to reactivate your Services. Certain
              data may also be retained as necessary to comply with legal obligations, resolve disputes, enforce
              agreements, support business operations, and enhance our Services. When data is retained for service
              improvements and development, all personally identifiable details are removed, and we use only aggregated
              insights rather than personal user data.
            </p>
            <p>
              <span>Content Shared Within the Services</span> If your account is deactivated or disabled, certain
              content and contributions you have made within the Services may remain accessible to team members or other
              users to ensure continuity and collaboration. For instance, user stories you have created, backlogs you
              have contributed, or comments you have made will still be displayed to relevant users. However, upon
              request, identifying details can be removed or anonymized in compliance with applicable privacy
              regulations.
            </p>
            <p>
              <span>Managed Accounts</span> If your access to the Services is provided through an organization, such as
              your employer, your information will be retained as required by the administrator of your account. For
              further details, please refer to the section on Managed Accounts and Administrators.
            </p>
            <p>
              <span>Marketing Information</span> If you have opted in to receive marketing communications from Bekind
              Labs, we will retain your marketing preferences for a reasonable period, measured from the date of your
              last recorded interaction with our Services—such as opening an email from us or logging into your Bekind
              Labs account. Additionally, we retain information derived from cookies and other tracking technologies for
              a reasonable period of time from the date such data was collected.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>Accessing and Managing Your Information</h2>
            <p>
              Bekind Labs provides users with various options to access, control, and manage personal information. The
              rights available to you depend on applicable laws and the jurisdiction in which you reside. These rights
              may include requesting details about how we process your data, obtaining a copy of your information we
              hold, objecting to the use of your data for certain purposes such as marketing, requesting deletion or
              restriction of your data, receiving your data in a structured, electronic format, requesting corrections
              or updates, and opting out of certain profiling activities. Some of these options can be exercised
              directly within the Services through your account settings. If the Services are managed on your behalf by
              an administrator, you may need to contact them for assistance. Your ability to exercise these rights may
              be subject to certain restrictions. We may be unable to fulfill a request if it involves disclosing
              another individual’s personal information or if we are required to retain certain data for legal or
              legitimate business interests. If you have authorized us to share your data with third-party service
              providers, such as by installing third-party apps, you will need to contact those providers directly to
              request deletion or restriction of your information. If you have unresolved concerns, you may have the
              right to file a complaint with the appropriate data protection authority in your country of residence,
              place of work, or where you believe your privacy rights have been violated. Where permitted under
              applicable laws, you may also appeal any decision we make regarding your request by contacting us at
              <a href={'mailto:"info@bekindlabs.com"'}>&nbsp;info@bekindlabs.com</a>.
            </p>
            <p>
              <span>Accessing and Updating Your Information</span>Bekind Labs allows users to access and modify certain
              personal details within the Services. Profile information can be updated through the account settings
              within the platform.
            </p>
            <p>
              <span>Deactivating Your Account</span> If you no longer wish to use our Services, you or your
              administrator may deactivate your account. If this option is available to you, it can be found in your
              account settings. Otherwise, you may need to contact your administrator. If you are an administrator and
              are unable to deactivate an account through the provided settings, you may reach out to
              info@bekindlabs.com for assistance. Deactivating your account does not automatically delete your personal
              information. Some of your past activity and content contributions may remain visible to other users. For
              information on permanently removing your data, please refer to the next section.
            </p>
            <p>
              <span>Deleting Your Information</span> Users have the ability to delete certain personal data directly
              within the Services. Specific content containing personal information can be removed through the
              platform’s editing and deletion functions. Profile information can also be modified or deleted within the
              account settings. However, certain records may need to be retained to comply with legal obligations,
              complete transactions, or fulfill regulatory requirements.
            </p>
            <p>
              <span>Request to Restrict Use of Your Information</span> In certain circumstances, you may request that we
              cease accessing, storing, using, or otherwise processing your information if you believe we lack the
              appropriate legal basis to do so. For instance, if a Tracker Boot account was created without your
              authorization or you are no longer an active user, you may request the deletion of your account in
              accordance with this policy. If you previously provided consent for the use of your information for a
              specific purpose, you may withdraw that consent by contacting us. However, this withdrawal will not affect
              any processing that has already taken place prior to your request. Please note that processing such
              requests may require time for verification and execution. In cases where there is a delay or a dispute
              regarding our right to process your information, we will suspend further processing until the request is
              honored or the dispute is resolved, unless otherwise instructed by your administrator. If you object to
              the disclosure of your information to a third-party application, please disable the application or request
              your administrator to do so.
            </p>
            <p>
              <span>Opting Out of Communications</span> You have the option to opt out of receiving promotional
              communications from Bekind Labs. This can be done by selecting the unsubscribe option in our emails or by
              directly contacting us to request removal from our promotional mailing list or registration database.
              However, even after opting out of promotional messages, you will continue to receive transactional emails
              regarding the Services. Certain notification messages may also be managed within your account settings,
              but please note that you will still receive generic advertisements.
            </p>
            <p>
              <span>Opting Out of Targeted Advertising</span> Where applicable under local regulations, you may have the
              right to opt out of targeted advertising. If you wish to opt out, you may follow the necessary steps
              outlined in your settings. Additionally, you may be able to opt out of personalized advertisements from
              companies that adhere to industry self-regulatory standards for online behavioral advertising.
            </p>
            <p>
              <span>Managing Cookie Preferences</span> You can control cookie settings through your web browser, which
              allows you to manage or disable cookies as per your preference. Please refer to Bekind Labs' Cookie
              Policy. By adjusting your browser settings, you can limit or block cookies used for tracking and targeted
              advertising.
            </p>
            <p>
              <span>“Do Not Track” (DNT) Signals</span> Some web browsers support “Do Not Track” (DNT) features, which
              signal to websites that you do not wish to be tracked. However, our websites may not respond to all
              browser-based DNT signals. That said, where required by applicable regulations, we do honor Global Privacy
              Control (GPC) settings, which allow users to opt out of certain data-sharing activities, including
              targeted advertising.
            </p>
            <p>
              <span>Data Portability</span> Data portability allows you to obtain certain personal data in a structured
              format that can be transferred to another service provider. While this applies to some categories of
              information, it does not cover all data types. If you request data portability, we will provide an
              electronic file containing your basic account details and any content you have created within the spaces
              under your sole control.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>How We Transfer Information</h2>
            <h3>International Data Transfers</h3>
            <p>
              <span>How We Transfer and Process Information Globally</span> Bekind Labs operates internationally, and as
              part of our global business operations, we may transfer, process, and store your information in countries
              outside of your country of residence, including locations where we or our third-party service providers
              operate. Whenever we transfer your information across borders, we implement appropriate safeguards to
              ensure compliance with applicable data protection regulations and to protect your personal data.
            </p>
            <p>
              <span>Transfers Within Bekind Labs Companies</span> To facilitate operational efficiency and service
              continuity, Bekind Labs transfers data between its entities and affiliates, such as between Japan, South
              Korea, and India. This ensures that authorized personnel in these locations can access necessary data for
              providing, maintaining, and improving our Services. However, as data protection laws differ across
              jurisdictions, some countries may not offer the same level of privacy safeguards as others. For transfers
              involving data originating from the European Economic Area (EEA), the United Kingdom (UK), or Switzerland,
              Bekind Labs relies on established data transfer mechanisms, including the Data Privacy Framework and
              Standard Contractual Clauses (SCCs) approved by the European Commission, to ensure compliance with
              international data protection standards.
            </p>
            <p>
              <span>Transfers to Third Parties</span> Bekind Labs engages third-party service providers, primarily based
              in the United States, to support our business operations. These providers offer essential services,
              including cloud infrastructure, payment processing, analytics, and customer support. When transferring
              customer data from the EEA, UK, or Switzerland to third parties in countries that may not have equivalent
              data protection laws, Bekind Labs ensures that appropriate safeguards are in place. These safeguards may
              include Standard Contractual Clauses (SCCs) approved by the European Commission, Binding Corporate Rules
              (BCRs) for data processors, or other legally recognized data protection mechanisms. Bekind Labs remains
              committed to ensuring that all international data transfers comply with applicable laws and regulatory
              requirements while maintaining the security, confidentiality, and integrity of user information.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>Additional Privacy Considerations</h2>
            <h3>Notice to End Users</h3>
            <p>
              Certain services offered by Bekind Labs, including Tracker Boot, are designed for use by organizations. If
              you are accessing the Services through an organization (e.g., your employer), that organization serves as
              the administrator of the Services and holds control over the accounts and/or Service sites associated with
              your use. In such cases, all inquiries related to data privacy, security, and access rights should be
              directed to your administrator, as your use of the Services is governed by the policies established by
              that organization. Bekind Labs is not responsible for the privacy or security practices of an
              administrator’s organization, which may differ from those outlined in this Privacy Policy.
            </p>
            <h3>Administrator Capabilities</h3>
            <p>
              <span>Member Administration:</span> Administrators can add or remove members, assign roles, and adjust
              permissions within the account. This ensures that only authorized individuals have access to specific
              projects and resources.
            </p>
            <p>
              <span>Account Settings Configuration:</span> They have the authority to modify account details, manage
              billing information, and oversee subscription plans, ensuring the account aligns with organizational
              requirements.
            </p>
            <p>
              <span>Project Settings Control:</span> Administrators can edit project settings, including iteration
              length, point scales, and integration configurations, tailoring each project to its specific workflow.
            </p>
            <p>
              <span>Member Role Assignment:</span> They can designate project roles such as Owner, Member, or Viewer,
              controlling the level of access and permissions each user has within a project.
            </p>
            <p>
              Even if your current use of the Services is not managed by an administrator, an organization may still
              assume administrative control over your account at a later date. For instance, if you use an
              organization-issued email address (such as your work email) to register for Tracker Boot, the owner of the
              email domain (e.g., your employer) may claim administrative control over your account and its associated
              data. Should this occur, you will receive notification of the change.
            </p>
            <p>If you do not wish to be subject to an organization’s administrative control, you should consider:</p>
            <ul>
              <li>
                • Using a personal email address to register for or access the Services instead of a work-issued email;
              </li>
              <li>
                • Deactivating your membership with any Tracker Boot project, team, or enterprise account that is
                administered by an organization;
              </li>
              <li>
                • Updating the email address associated with your account (provided an administrator has not yet assumed
                control).
              </li>
            </ul>
            <p>
              Once an administrator takes control over your account, you will no longer be able to change your email
              address or access permissions without the administrator’s approval.
            </p>
            <p>
              For further information, please refer to your organization’s IT or administrative policies or contact your
              administrator directly.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>Additional Disclosures for California Residents</h2>
            <p>
              This section applies exclusively to residents of California and supplements the Bekind Labs Privacy
              Policy, in accordance with the California Consumer Privacy Act (CCPA) and the California Privacy Rights
              Act (CPRA). Beginning April 1st, 2025, we may collect and process the following categories of personal
              information from California residents, as permitted under applicable law:
            </p>
            <ul>
              <li>
                Identifiers: Name, email address, IP address, and unique identifiers associated with a user account.
              </li>
              <li>Commercial Information: Records of transactions, purchase history, and billing details.</li>
              <li>
                Internet or Other Electronic Network Activity: Information related to the use of our Services, including
                device details, browser type, and interactions with our websites.
              </li>
              <li>
                Professional or Employment-Related Information: Job title, company name, and industry affiliation.
              </li>
              <li>Geolocation Data: Approximate location derived from IP addresses.</li>
              <li>
                Inferences Drawn from Other Personal Information: User preferences, behavioral patterns, and interests
                based on interactions with our Services.
              </li>
            </ul>
            <p>
              Bekind Labs does not sell personal information in exchange for monetary compensation. However, under
              California law, certain disclosures—such as cross-context behavioral advertising (e.g., third-party
              tracking for personalized ads)—may be classified as "sharing" of personal information.
            </p>
            <h3>Sensitive Personal Information</h3>
            <p>
              Bekind Labs does not use or disclose sensitive personal information other than permitted under applicable
              laws.
            </p>
            <p>
              <span>Data Retention</span> We retain your personal information for as long as necessary to fulfill the
              business purposes outlined in this policy, or as required by legal or regulatory obligations. The duration
              of retention depends on several factors, including compliance with recordkeeping requirements, the need to
              address inquiries or disputes, and whether there is an ongoing relationship with you. For specific data
              types, Bekind Labs retains:
            </p>
            <ul>
              <li>
                Account Information: Retained for as long as your account remains active and a reasonable period
                thereafter in case you decide to reactivate your Services.
              </li>
              <li>
                Transaction and Billing Data: Retained for up to 7 years for tax, accounting, and legal compliance
                purposes.
              </li>
              <li>
                Support Data and Communications: Retained for up to 3 years to assist with ongoing service improvements
                and dispute resolution.
              </li>
              <li>
                Marketing Information: Retained until you opt-out, with a maximum retention period of 2 years from your
                last interaction.
              </li>
              <li>
                If immediate deletion is not technically feasible (such as data stored in backup archives), we will
                securely isolate and store the data until deletion is possible.
              </li>
            </ul>
            <p>
              <span>Exercising Your Rights</span> If you are a California resident, you may have additional rights under
              the California Consumer Privacy Act (CCPA). These rights include such as the rights to request information
              about how we process your personal information, request deletion and the right to request access to the
              specific pieces of categories of personal information we have collected about you. For instructions on how
              to exercise these rights, please refer to the section “<b>How to access and control your information.</b>”
              We encourage you to manage your personal data and take advantage of the privacy controls provided in our
              Services. Bekind Labs does not discriminate against users who exercise their CCPA privacy rights. To
              ensure the security of your data, we may require identity verification before processing requests to
              access, correct, or delete your information. If we cannot verify your identity, we may be unable to
              fulfill such requests.
            </p>
            <p>
              <span>Opting Out of Certain Disclosures to Third Parties</span> YYou may choose to opt out of the “sale”
              or “sharing” of personal information for cross-context behavioral advertising by following the
              instructions provided in your privacy settings. Where required, we also honor opt-out requests submitted
              via privacy preference signals, such as the Global Privacy Control (GPC). For further details on GPC and
              how to implement it in your browser, visit globalprivacycontrol.org. Starting February 25, 2025, Bekind
              Labs will begin sharing data with selected partners for limited business purposes. Users will be able to
              manage these settings within their account privacy controls or by contacting
              <a href={'mailto:"privacy@bekindlabs.com'}>&nbsp;privacy@bekindlabs.com</a>.
            </p>
            <p>
              <span>Authorized Agents</span> You may appoint an authorized agent to submit requests on your behalf. To
              do so, you must provide the agent with written permission, and we may request a copy of this authorization
              when processing their request. In addition, we may require you to verify your own identity and confirm
              that you have granted permission to the authorized agent.
            </p>
            <p>
              <span>Age Restrictions and Children’s Privacy</span> Tracker Boot is intended for users aged 16 and above.
              Users under 16 may access the Service only with the verifiable consent of a parent or legal guardian or
              under the supervision of an authorized educational institution. If a user under 16 wishes to use the
              Service, they must either obtain prior approval or be enrolled in a supervised educational program. In
              compliance with applicable laws such as COPPA, Tracker Boot is not intended for children under the age of
              13. We do not knowingly collect personal information from children under 13. If we become aware that a
              child under 13 has submitted personal data without appropriate consent or supervision, we will take steps
              to delete the information promptly.
            </p>
            <p>
              <span>International Data Transfers</span> Bekind Labs operates globally and, in order to ensure seamless
              service delivery, may transfer, process, and store user data in Japan, South Korea, India, the United
              States, and other jurisdictions where Bekind Labs or third-party service providers operate. These
              transfers are conducted in accordance with applicable data protection laws, including GDPR, APPI, PIPA,
              and DPDPA.
            </p>
            <p>
              To facilitate business operations, Bekind Labs transfers personal data between its corporate affiliates in
              Japan, South Korea, India, and the United States. These entities comply with regional privacy laws, and
              data transfers between them are protected through legally recognized mechanisms, including Standard
              Contractual Clauses (SCCs) approved by the European Commission and other safeguards required under GDPR,
              CCPA/CPRA, and APPI.
            </p>
            <p>
              Certain third-party service providers, cloud storage providers, and analytics partners process data on
              behalf of Bekind Labs in jurisdictions that may not have equivalent privacy protections to those in Japan,
              South Korea, India, the EEA, or the United States. To ensure the security of such transfers, Bekind Labs
              implements legally recognized safeguards such as Standard Contractual Clauses (SCCs) for data transfers
              outside the EEA, Binding Corporate Rules (BCRs) for transfers to data processors in regions without
              adequate data protection laws, and contractual agreements requiring service providers to comply with
              applicable privacy regulations. Further details regarding cross-border data transfers may be obtained by
              contacting the Bekind Labs Privacy Team.
            </p>
            <p>
              <span>Changes to Our Privacy Policy</span> Bekind Labs may update this privacy policy from time to time.
              Any changes will be posted on this page, and if significant modifications are made, we will provide
              additional notice through:
            </p>
            <ul>
              <li>• Announcements on our Services’ homepages or login screens</li>
              <li>• Direct email notifications</li>
            </ul>
            <p>
              We will also maintain an archive of previous versions of this Privacy Policy for your reference. We
              encourage you to review this policy regularly to stay informed about how we handle personal information.
              If you do not agree with any changes to this Privacy Policy, you may stop using the Services and
              deactivate your account as outlined in this policy.
            </p>
          </div>
        </div>
      </div>
      <LandingFooter />
    </>
  )
}
